import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, Infinity as InfinityIcon, Gift, Copy, Link as LinkIcon } from "lucide-react";
import { fireBoostConfetti } from "@/lib/confetti";
import { useLocale } from "@/i18n/LocaleContext";

export default function VoucherCard({ showRedeem = false }) {
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [giftDialog, setGiftDialog] = useState(null);
  const [giftCode, setGiftCode] = useState(null);
  const [gifting, setGifting] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(() => {
    api.get("/vouchers/mine").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  if (!data) return null;
  const hasVouchers = data.unlimited_active || data.vouchers?.length > 0 || data.gifted?.length > 0;
  if (!hasVouchers && !showRedeem && !giftDialog) return null;

  const activate = async (id) => {
    try {
      const r = await api.post(`/vouchers/${id}/activate`);
      fireBoostConfetti();
      toast.success(`Unlimited date offers active until ${new Date(r.data.unlimited_until).toLocaleDateString()}!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate voucher");
    }
  };

  const confirmGift = async () => {
    if (gifting) return;
    setGifting(true);
    try {
      const r = await api.post(`/vouchers/${giftDialog.voucher_id}/gift`);
      setGiftCode(r.data.gift_code);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the gift code");
    } finally {
      setGifting(false);
    }
  };

  const copy = async (text, msg) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* noop */
    }
    toast.success(msg);
  };

  const redeem = async () => {
    if (!redeemCode.trim() || redeeming) return;
    setRedeeming(true);
    try {
      const r = await api.post("/vouchers/redeem", { gift_code: redeemCode.trim() });
      fireBoostConfetti();
      toast.success(r.data?.promo ? "Promo code redeemed — rewards added to your account! 🎁" : "Gift voucher added to your account! 🎁");
      setRedeemCode("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not redeem this code");
    } finally {
      setRedeeming(false);
    }
  };

  const giftLink = giftCode ? `${window.location.origin}/register?gift=${giftCode}` : "";

  return (
    <div className="mb-6 p-5 rounded-2xl bg-amber-500/5 border border-amber-500/25" data-testid="voucher-card">
      {data.unlimited_active ? (
        <div className="flex items-center gap-3" data-testid="unlimited-active-banner">
          <InfinityIcon className="w-6 h-6 text-amber-400 shrink-0" />
          <div>
            <p className="font-semibold text-amber-400">{t("voucher.unlimitedActive")}</p>
            <p className="text-sm text-muted-foreground">
              {t("voucher.unlimitedUntil").replace("{date}", new Date(data.unlimited_until).toLocaleDateString())}
            </p>
          </div>
        </div>
      ) : data.vouchers?.length > 0 ? (
        <div className="space-y-4">
          {data.vouchers.map((v) => (
            <div key={v.voucher_id} className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                {v.gifted_from ? (
                  <Gift className="w-6 h-6 text-amber-400 shrink-0" />
                ) : (
                  <Trophy className="w-6 h-6 text-amber-400 shrink-0" />
                )}
                <div>
                  <p className="font-semibold">
                    {v.gifted_from
                      ? t("voucher.giftFrom").replace("{name}", v.gifted_from)
                      : t("voucher.championPrize")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {v.gifted_from
                      ? `A friend's champion prize — activate any time before ${new Date(v.expires_at).toLocaleDateString()}`
                      : `Won for #1 in "${v.section}" — activate any time before ${new Date(v.expires_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  className="bg-amber-500 text-black hover:bg-amber-400"
                  onClick={() => activate(v.voucher_id)}
                  data-testid={`activate-voucher-${v.voucher_id}`}
                >
                  {t("voucher.activate")}
                </Button>
                {!v.gifted_from && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                    onClick={() => {
                      setGiftCode(null);
                      setGiftDialog(v);
                    }}
                    data-testid={`gift-voucher-${v.voucher_id}`}
                  >
                    <Gift className="w-4 h-4 mr-1.5" /> {t("voucher.giftIt")}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {showRedeem && !data.unlimited_active && (
        <div className={hasVouchers ? "mt-4 pt-4 border-t border-amber-500/15" : ""} data-testid="redeem-gift-section">
          <p className="text-sm font-medium mb-2 flex items-center gap-2">
            <Gift className="w-4 h-4 text-amber-400" /> {t("voucher.haveCode")}
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="GIFT-XXXXXXXX"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              className="bg-white/5 border-white/10"
              data-testid="redeem-gift-input"
            />
            <Button
              onClick={redeem}
              disabled={redeeming || !redeemCode.trim()}
              className="bg-amber-500 text-black hover:bg-amber-400 shrink-0"
              data-testid="redeem-gift-btn"
            >
              {redeeming ? "..." : t("voucher.redeem")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Redeem a champion voucher a friend gifted you — 2 weeks of unlimited date offers.
          </p>
        </div>
      )}

      {data.gifted?.length > 0 && (
        <div
          className={data.unlimited_active || data.vouchers?.length > 0 || (showRedeem && !data.unlimited_active) ? "mt-4 pt-4 border-t border-amber-500/15" : ""}
          data-testid="gifted-vouchers-section"
        >
          <p className="text-sm font-medium mb-2 flex items-center gap-2">
            <Gift className="w-4 h-4 text-amber-400" /> {t("voucher.giftsSent")}
          </p>
          <div className="space-y-2.5">
            {data.gifted.map((g) => (
              <div key={g.gift_code} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`gifted-voucher-${g.gift_code}`}>
                <div className="min-w-0">
                  <p className="text-sm font-mono tracking-wider">{g.gift_code}</p>
                  <p className="text-xs text-muted-foreground">
                    2 weeks of unlimited offers · sent {new Date(g.gifted_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {g.status === "claimed" ? (
                    <span
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/30"
                      data-testid={`gift-status-${g.gift_code}`}
                    >
                      ✓ Claimed by {g.claimed_by || "a friend"}
                    </span>
                  ) : g.status === "opened" ? (
                    <span
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-sky-500/15 text-sky-400 border border-sky-500/30"
                      data-testid={`gift-status-${g.gift_code}`}
                    >
                      👀 Link opened
                    </span>
                  ) : (
                    <span
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30"
                      data-testid={`gift-status-${g.gift_code}`}
                    >
                      ⏳ Waiting to be claimed
                    </span>
                  )}
                  {g.status !== "claimed" && (
                    <button
                      type="button"
                      onClick={() => copy(`${window.location.origin}/register?gift=${g.gift_code}`, "Gift link copied!")}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-amber-400 hover:bg-white/5 transition-colors"
                      title="Copy gift link again"
                      data-testid={`copy-gifted-link-${g.gift_code}`}
                    >
                      <LinkIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!giftDialog} onOpenChange={(o) => !o && setGiftDialog(null)}>
        <DialogContent className="max-w-sm bg-card border-white/10" data-testid="gift-voucher-dialog">
          {!giftCode ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-amber-400" /> Gift your voucher
                </DialogTitle>
                <DialogDescription>
                  Turn your unused champion voucher into a gift code a friend can redeem — even if they're new to the
                  app. Once gifted, you won't be able to use it yourself.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2 justify-end mt-2">
                <Button variant="outline" onClick={() => setGiftDialog(null)} data-testid="cancel-gift-btn">
                  Cancel
                </Button>
                <Button
                  className="bg-amber-500 text-black hover:bg-amber-400"
                  onClick={confirmGift}
                  disabled={gifting}
                  data-testid="confirm-gift-btn"
                >
                  {gifting ? "Creating..." : "Create gift code"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>🎁 Gift code created!</DialogTitle>
                <DialogDescription>
                  Share it with your friend — they redeem it in Settings, or the link signs them up with the gift
                  attached.
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-center">
                <p className="text-2xl font-bold tracking-widest text-amber-400" data-testid="gift-code-value">
                  {giftCode}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => copy(giftCode, "Gift code copied!")} data-testid="copy-gift-code-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy code
                </Button>
                <Button variant="outline" onClick={() => copy(giftLink, "Gift link copied!")} data-testid="copy-gift-link-btn">
                  <LinkIcon className="w-4 h-4 mr-2" /> Copy link
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
